import React, { useState, useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import FormInputField from "@/components/lib/from-input-field";
import { useForm, useFieldArray } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { createShift, updateShift } from "@/api/new-api/shift/shift";
import { toast } from "react-hot-toast";
import { restaurant_id } from "@/api/lib/url";
import useAuth from "@/hooks/useAuth";

const newShiftSchema = z.object({
  // emploayId: z.string().min(1, "Employee ID is required"),
  shiftType: z.string().min(1, "Shift type is required"),
  days: z.string().min(1, "Day is required"),
  shiftTimes: z
    .array(
      z.object({
        start: z.string().min(1, "Start time is required"),
        end: z.string().min(1, "End time is required"),
      })
    )
    .min(1, "At least one shift time is required"),
});

export function NewShiftDialog({ openNewShiftDialog, offDialog, setShifts }) {
  const { user } = useAuth();
  const form = useForm({
    resolver: zodResolver(newShiftSchema),
    defaultValues: {
      emploayId: "",
      shiftType: "",
      days: "",
      shiftTimes: [{ start: "", end: "" }],
    },
  });
  const [loading, setLoading] = useState(false);

  const {
    fields: shiftFields,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: "shiftTimes",
  });

  const addShift = async (data) => {
    setLoading(true);
    const formattedData = {
      restaurant_id: user?.restaurant_id,
      shift_type: data.shiftType,
      day: data.days,
      shifts: data?.shiftTimes?.map((time) => ({
        start_time: time.start,
        end_time: time.end,
      })),
    };

    try {
      const response = await createShift(formattedData);
      if (response?.status === "success") {
        setShifts((prevShifts) => [
          ...prevShifts,
          response.data.shift_schedule,
        ]);
        offDialog();
        toast.success(response?.message || "Shift Created Successfull");
      } else {
        toast.error(response.message || "Error");
      }
    } catch (error) {
      toast.error(error.message || "Error submittin Shift");
      console.error("Error submitting shift:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectOptionTime = Array.from({ length: 24 }, (_, i) => {
    const hour24 = i.toString().padStart(2, "0") + ":00";
    const hour12 = i % 12 === 0 ? 12 : i % 12;
    const period = i < 12 ? "AM" : "PM";
    return {
      label: `${hour12.toString().padStart(2, "0")}:00 ${period}`,
      value: hour24,
    };
  });

  return (
    <Dialog open={openNewShiftDialog} onOpenChange={offDialog}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-auto">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            Add New Shift
          </DialogTitle>
          <Separator className="mt-4" />
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(addShift)}
              className="flex flex-col gap-5 mt-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
                {/* <FormInputField
                  name="emploayId"
                  style="bg-cblack3"
                  placeholder="Enter Employee ID"
                  form={form}
                  label="Employee ID"
                /> */}
                <FormInputField
                  name="shiftType"
                  style="bg-cblack3"
                  placeholder="Select Here"
                  form={form}
                  type="select"
                  selectOption={[
                    { label: "Weekly", value: "weekly" },
                    { label: "Daily", value: "daily" },
                  ]}
                  label="Shift Type"
                />
                <FormInputField
                  name="days"
                  style="bg-cblack3"
                  placeholder="Select Here"
                  form={form}
                  type="select"
                  selectOption={[
                    { label: "Monday", value: "monday" },
                    { label: "Tuesday", value: "tuesday" },
                    { label: "Wednesday", value: "wednesday" },
                    { label: "Thursday", value: "thursday" },
                    { label: "Friday", value: "friday" },
                    { label: "Saturday", value: "saturday" },
                    { label: "Sunday", value: "sunday" },
                  ]}
                  label="Days"
                />
              </div>

              <div className="space-y-3">
                <h2>Shift Times</h2>
                <Separator />
              </div>

              {shiftFields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-1 md:grid-cols-2 gap-5"
                >
                  <FormInputField
                    name={`shiftTimes.${index}.start`}
                    style="bg-cblack3"
                    placeholder="Start Time"
                    form={form}
                    type="select"
                    selectOption={selectOptionTime}
                    label={`Starting ${index + 1}`}
                  />
                  <FormInputField
                    name={`shiftTimes.${index}.end`}
                    style="bg-cblack3"
                    placeholder="End Time"
                    form={form}
                    type="select"
                    selectOption={selectOptionTime}
                    label={`Ending ${index + 1}`}
                  />
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => remove(index)}
                      className="w-32"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                className="w-32"
                onClick={() => append({ start: "", end: "" })}
              >
                Add More
              </Button>

              <Button
                isLoading={loading}
                className="bg-cyellow w-full mt-2 text-cblack"
              >
                Confirm
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const updateShiftSchema = z.object({
  shiftType: z.string().min(1, "Shift type is required"),
  days: z.string().min(1, "Day is required"),
  shiftTimes: z
    .array(
      z.object({
        start: z.string().min(1, "Start time is required"),
        end: z.string().min(1, "End time is required"),
      })
    )
    .min(1, "At least one shift time is required"),
});

export function UpdateShiftDialog({
  openUpdateShiftDialog,
  offDialog,
  setShifts,
  selectedDay,
  shiftData = {},
}) {
  const form = useForm({
    resolver: zodResolver(updateShiftSchema),
    defaultValues: {
      shiftType: "",
      days: "",
      shiftTimes: [{ start: "", end: "" }],
    },
  });

  const [loading, setLoading] = useState(false);
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: "shiftTimes",
  });

  useEffect(() => {
    if (shiftData?.id) {
      form.reset({
        shiftType: shiftData.shift_type || "",
        days: shiftData.day || "",
        shiftTimes: shiftData.shifts?.map((s) => ({
          start: s.start_time.slice(0, 5),
          end: s.end_time.slice(0, 5),
        })) || [{ start: "", end: "" }],
      });
      replace(
        shiftData.shifts?.map((s) => ({
          start: s.start_time.slice(0, 5),
          end: s.end_time.slice(0, 5),
        })) || [{ start: "", end: "" }]
      );
    }
  }, [shiftData]);

  const selectOptionTime = Array.from({ length: 24 }, (_, i) => {
    const hour24 = i.toString().padStart(2, "0") + ":00";
    const hour12 = i % 12 === 0 ? 12 : i % 12;
    const period = i < 12 ? "AM" : "PM";
    return {
      label: `${hour12.toString().padStart(2, "0")}:00 ${period}`,
      value: hour24,
    };
  });

  const onSubmit = async (data) => {
    setLoading(true);
    const payload = {
      restaurant_system_id: restaurant_id,
      shift_type: data.shiftType,
      day: data.days,
      shifts: data.shiftTimes.map((s) => ({
        start_time: s.start,
        end_time: s.end,
      })),
    };
    if (!shiftData.id) return;
    try {
      const res = await updateShift(shiftData?.id, payload);
      if (res?.status === "success") {
        const updatedShift = res.data.shift_schedule;
        setShifts((prevShifts) =>
          prevShifts.map((shift) =>
            shift.id === updatedShift.id ? updatedShift : shift
          )
        );
        toast.success("Shift updated successfully");
        offDialog();
      } else {
        toast.error(res?.message || "Failed to update shift");
      }
    } catch (err) {
      toast.error("An error occurred while updating shift");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={openUpdateShiftDialog} onOpenChange={offDialog}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-auto">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium capitalize">
            Update Shift {selectedDay || "No day here"}
          </DialogTitle>
          <Separator className="mt-4" />
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col gap-5 mt-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
                <FormInputField
                  name="shiftType"
                  style="bg-cblack3"
                  placeholder="Select Here"
                  form={form}
                  type="select"
                  selectOption={[
                    { label: "Weekly", value: "weekly" },
                    { label: "Daily", value: "daily" },
                  ]}
                  label="Shift Type"
                />
                <FormInputField
                  name="days"
                  style="bg-cblack3"
                  placeholder="Select Here"
                  form={form}
                  type="select"
                  selectOption={[
                    { label: "Monday", value: "monday" },
                    { label: "Tuesday", value: "tuesday" },
                    { label: "Wednesday", value: "wednesday" },
                    { label: "Thursday", value: "thursday" },
                    { label: "Friday", value: "friday" },
                    { label: "Saturday", value: "saturday" },
                    { label: "Sunday", value: "sunday" },
                  ]}
                  label="Days"
                />
              </div>

              {fields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-1 md:grid-cols-2 gap-5 items-end"
                >
                  <FormInputField
                    name={`shiftTimes.${index}.start`}
                    style="bg-cblack3"
                    placeholder="Start Time"
                    form={form}
                    type="select"
                    selectOption={selectOptionTime}
                    label={`Start ${index + 1}`}
                  />
                  <FormInputField
                    name={`shiftTimes.${index}.end`}
                    style="bg-cblack3"
                    placeholder="End Time"
                    form={form}
                    type="select"
                    selectOption={selectOptionTime}
                    label={`End ${index + 1}`}
                  />
                  {index > 0 && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => remove(index)}
                      className="w-32"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}

              <Button
                type="button"
                variant="outline"
                className="w-32"
                onClick={() => append({ start: "", end: "" })}
              >
                Add More
              </Button>

              <Button
                isLoading={loading}
                className="bg-cgreen w-full mt-4 text-cblack"
              >
                Confirm
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
